import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Printer, IndianRupee, Percent } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill, KpiCard } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PrintPreview, HospitalInvoiceDoc } from "@/components/PrintDoc";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, titleize } from "@/lib/format";

const EMPTY_LINE = { description: "", detail: "", quantity: "1", rate: "", tax_percent: "0" };
const PAYMENT_MODES = ["cash", "upi", "card", "bank", "insurance", "cheque", "online", "other"];
const EMPTY_FORM = {
  patient_id: "", invoice_type: "consultation", reference_id: "", doctor_id: "",
  fee_type: "new", include_consultation: false, payment_method: "cash", payment_reference: "",
  collection_mode: "due", initial_payments: [], notes: "",
  discount: "0", discount_type: "amount", tax_percent: "0", lines: [{ ...EMPTY_LINE }],
};

const lineTotals = (line) => {
  const gross = Number(line.quantity || 0) * Number(line.rate || 0);
  const taxable = Math.max(gross, 0);
  const tax = taxable * Number(line.tax_percent || 0) / 100;
  return { taxable, tax, total: taxable + tax };
};

export default function HospitalBilling() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [feeNote, setFeeNote] = useState("");
  const [pay, setPay] = useState(null);
  const [payForm, setPayForm] = useState({
    payments: [{ amount: "", mode: "cash", reference_no: "", note: "" }],
  });
  const [voidFor, setVoidFor] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/hospital/invoices", {
        params: { q: q || undefined, page, limit: 25 },
      });
      setItems(data.items);
      setTotal(data.total || 0);
    } catch (e) { setError(apiError(e)); }
  }, [page, q]);

  useEffect(() => {
    if (!can("hospital.billing.manage")) return;
    load();
    Promise.all([api.get("/hospital/patients", { params: { limit: 300 } }), api.get("/hospital/doctors")])
      .then(([p, d]) => { setPatients(p.data.items); setDoctors(d.data.items); }).catch(() => {});
  }, [load, can]);

  const totals = useMemo(() => {
    const lines = form.lines.map(lineTotals);
    const subtotal = lines.reduce((s, l) => s + l.taxable, 0);
    const lineTax = lines.reduce((s, l) => s + l.tax, 0);
    const consultation = form.include_consultation
      ? Number(doctors.find((d) => d.doctor_id === form.doctor_id)?.[
        { new: "consultation_fee", follow_up: "follow_up_fee", emergency: "emergency_fee" }[form.fee_type]] || 0)
      : 0;
    const sub = subtotal + consultation;
    const billTax = sub * Number(form.tax_percent || 0) / 100;
    const tax = lineTax + billTax;
    const invoiceValue = sub + tax;
    const discount = form.discount_type === "percent"
      ? invoiceValue * Number(form.discount || 0) / 100
      : Number(form.discount || 0);
    return {
      subtotal: sub,
      tax,
      discount,
      invoiceValue,
      total: Math.max(invoiceValue - discount, 0),
      consultation,
    };
  }, [form, doctors]);

  if (!can("hospital.billing.manage")) return <NoAccess what="hospital billing" />;

  const setLine = (idx, patch) => {
    const lines = form.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    setForm({ ...form, lines });
  };

  const pickDoctor = async (doctorId, feeType) => {
    setForm((f) => ({ ...f, doctor_id: doctorId, fee_type: feeType, include_consultation: Boolean(doctorId) }));
    setFeeNote("");
    if (!doctorId) return;
    try {
      const { data } = await api.get(`/hospital/doctors/${doctorId}/fee`, { params: { fee_type: feeType } });
      setFeeNote(data.configured
        ? `${titleize(feeType)} consultation fee for Dr. ${data.doctor_name}: ${inr(data.fee)}`
        : `Consultation fee not configured for Dr. ${data.doctor_name}.`);
    } catch (e) { setFeeNote(apiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    if (!form.patient_id) { setFormError("Patient is required."); return; }
    const lines = form.lines.filter((l) => l.description.trim() && Number(l.rate) > 0)
      .map((l) => ({ description: l.description.trim(), detail: l.detail,
        quantity: Number(l.quantity || 1), rate: Number(l.rate),
        tax_percent: Number(l.tax_percent || 0) }));
    if (!lines.length && !form.include_consultation) {
      setFormError("Add at least one billing line item with a rate."); return;
    }
    const discountValue = Number(form.discount || 0);
    if (form.discount_type === "percent" && discountValue > 100) {
      setFormError("Percentage discount cannot exceed 100%."); return;
    }
    if (form.discount_type === "amount" && discountValue > totals.invoiceValue + 0.01) {
      setFormError("Bill discount cannot exceed the applicable invoice amount."); return;
    }
    const initialPayments = form.collection_mode === "full"
      ? totals.total > 0
        ? [{ amount: totals.total, mode: form.payment_method, reference_no: form.payment_reference }]
        : []
      : form.collection_mode === "split"
        ? form.initial_payments.map((payment) => ({
          ...payment,
          amount: Number(payment.amount || 0),
        }))
        : [];
    const allocated = initialPayments.reduce((sum, payment) => sum + payment.amount, 0);
    if (form.collection_mode === "split" && initialPayments.length < 2) {
      setFormError("Add at least two payment modes for a split payment."); return;
    }
    if (initialPayments.some((payment) => payment.amount <= 0) || allocated > totals.total + 0.01) {
      setFormError("Payment allocations must be greater than zero and cannot exceed the total."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/hospital/invoices", {
        patient_id: form.patient_id, invoice_type: form.invoice_type,
        reference_id: form.reference_id, doctor_id: form.doctor_id || "",
        fee_type: form.fee_type, include_consultation: form.include_consultation,
        items: lines, discount: Number(form.discount || 0), discount_type: form.discount_type,
        tax_percent: Number(form.tax_percent || 0),
        payment_method: form.payment_method, initial_payments: initialPayments, notes: form.notes });
      toast.success(`Invoice ${data.invoice.invoice_no} created for ${inr(data.invoice.total)}.`);
      setOpen(false); setForm(EMPTY_FORM); setFeeNote(""); load();
      openPreview(data.invoice.invoice_id);
    } catch (err) { setFormError(apiError(err)); } finally { setBusy(false); }
  };

  const openPreview = async (invoiceId) => {
    try {
      const { data } = await api.get(`/hospital/invoices/${invoiceId}`);
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    try {
      const payments = payForm.payments.map((payment) => ({
        ...payment,
        amount: Number(payment.amount || 0),
      }));
      await api.post(`/hospital/invoices/${pay.invoice_id}/split-payments`, { payments });
      toast.success(payments.length > 1 ? "Split payment recorded." : "Payment recorded.");
      setPay(null);
      setPayForm({ payments: [{ amount: "", mode: "cash", reference_no: "", note: "" }] });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const submitVoid = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hospital/invoices/${voidFor.invoice_id}/void`, { reason: voidReason });
      toast.success("Invoice voided with an audit entry.");
      setVoidFor(null); setVoidReason(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const summary = (items || []).reduce((acc, i) => {
    if (i.status === "void") return acc;
    acc.total += i.total; acc.paid += i.paid; acc.due += i.outstanding;
    return acc;
  }, { total: 0, paid: 0, due: 0 });
  const paymentTotal = payForm.payments.reduce((sum, payment) => {
    return sum + Number(payment.amount || 0);
  }, 0);
  const updatePayment = (index, patch) => {
    setPayForm((current) => ({
      payments: current.payments.map((payment, paymentIndex) => {
        return paymentIndex === index ? { ...payment, ...patch } : payment;
      }),
    }));
  };
  const updateInitialPayment = (index, patch) => {
    setForm((current) => ({
      ...current,
      initial_payments: current.initial_payments.map((payment, paymentIndex) => {
        return paymentIndex === index ? { ...payment, ...patch } : payment;
      }),
    }));
  };
  const initialPaymentTotal = form.initial_payments.reduce((sum, payment) => {
    return sum + Number(payment.amount || 0);
  }, 0);

  return (
    <div data-testid="hospital-billing-page">
      <PageHeader overline="Hospital" title="Billing & invoices"
        subtitle="Multi-line invoices with server-computed totals. Invoices are never deleted — corrections are recorded as voids with a reason."
        actions={<button onClick={() => { setForm(EMPTY_FORM); setFeeNote(""); setOpen(true); }}
          className={primaryBtnCls} data-testid="add-invoice-btn">Create invoice</button>} />

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-3 mb-8">
            <KpiCard label="Billed" value={inr(summary.total)} onClick={() => {}} testid="billing-kpi-total" />
            <KpiCard label="Collected" value={inr(summary.paid)} tone="success" onClick={() => {}} testid="billing-kpi-paid" />
            <KpiCard label="Outstanding" value={inr(summary.due)} tone="danger" onClick={() => {}} testid="billing-kpi-due" />
          </div>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search invoice, patient or UHID" data-testid="invoice-search-input"
              className={`${inputCls} max-w-sm`} />
            <p className="text-xs text-slate-500" data-testid="invoice-search-count">
              {total} invoice{total === 1 ? "" : "s"}
            </p>
          </div>
          <Table testid="hospital-invoices-table" columns={["Invoice", "Patient", "Type", "Items", "Total", "Paid", "Outstanding", "Status", "Date", "Actions"]}
            rows={items} empty={<EmptyState title="No invoices yet" subtitle="Create a consultation, OPD, IPD or procedure invoice." testid="invoices-empty" />}
            renderRow={(i) => (
              <tr key={i.invoice_id} data-testid={`invoice-row-${i.invoice_id}`}>
                <Td className="font-mono-num text-slate-900">{i.invoice_no}</Td>
                <Td className="font-medium text-slate-900">
                  {i.patient_name}
                  {i.patient_uhid && (
                    <span className="block text-xs text-slate-400">{i.patient_uhid}</span>
                  )}
                </Td>
                <Td className="text-slate-600 capitalize">{i.invoice_type}</Td>
                <Td className="text-slate-600">{(i.items || []).length}</Td>
                <Td className="text-slate-900" data-testid={`invoice-total-${i.invoice_id}`}>{inr(i.total)}</Td>
                <Td className="text-slate-600">{inr(i.paid)}</Td>
                <Td className={i.outstanding > 0 ? "text-red-600" : "text-slate-500"}>{inr(i.outstanding)}</Td>
                <Td><StatusPill status={i.status} /></Td>
                <Td className="text-slate-500 whitespace-nowrap">{dmy(i.created_at)}</Td>
                <Td>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => openPreview(i.invoice_id)} className={ghostBtnCls} data-testid={`print-invoice-${i.invoice_id}`}>
                      <Printer className="h-3.5 w-3.5" /> Print
                    </button>
                    {i.status !== "void" && i.outstanding > 0 && can("hospital.payments.manage") && (
                      <button onClick={() => {
                        setPay(i);
                        setPayForm({
                          payments: [{ amount: String(i.outstanding), mode: "cash", note: "" }],
                        });
                      }}
                        className={ghostBtnCls} data-testid={`pay-${i.invoice_id}`}>Collect</button>
                    )}
                    {i.status !== "void" && (
                      <button onClick={() => setVoidFor(i)} className={ghostBtnCls} data-testid={`void-${i.invoice_id}`}>Void</button>
                    )}
                  </div>
                </Td>
              </tr>
            )} />
          <div className="mt-5 flex items-center justify-end gap-3" data-testid="invoice-pagination">
            <button type="button" className={ghostBtnCls} disabled={page <= 1}
              data-testid="invoice-page-prev" onClick={() => setPage((value) => value - 1)}>
              Previous
            </button>
            <span className="text-sm text-slate-600" data-testid="invoice-page-label">
              Page {page} of {Math.max(Math.ceil(total / 25), 1)}
            </span>
            <button type="button" className={ghostBtnCls} disabled={page * 25 >= total}
              data-testid="invoice-page-next" onClick={() => setPage((value) => value + 1)}>
              Next
            </button>
          </div>
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Create invoice" wide
        subtitle="Line totals, tax and the grand total are calculated by the server before the bill is saved."
        testid="add-invoice-modal">
        <form onSubmit={submit} className="space-y-7">
          <div className="grid gap-5 sm:grid-cols-4">
            <Field label="Patient *" className="sm:col-span-2">
              <select className={inputCls} value={form.patient_id} data-testid="invoice-patient-select"
                onChange={(e) => setForm({ ...form, patient_id: e.target.value })}>
                <option value="">Select patient</option>
                {patients.map((p) => <option key={p.patient_id} value={p.patient_id}>{p.name} · {p.uhid}</option>)}
              </select>
            </Field>
            <Field label="Bill type">
              <select className={inputCls} value={form.invoice_type} data-testid="invoice-type-select"
                onChange={(e) => setForm({ ...form, invoice_type: e.target.value })}>
                {["consultation", "opd", "ipd", "procedure", "service", "other"].map((t) => <option key={t} value={t}>{titleize(t)}</option>)}
              </select>
            </Field>
            <Field label="Full payment mode">
              <select className={inputCls} value={form.payment_method} data-testid="invoice-payment-method-select"
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                {PAYMENT_MODES.map((mode) => <option key={mode} value={mode}>{titleize(mode)}</option>)}
              </select>
            </Field>
            <Field label="Collection at invoice creation">
              <select className={inputCls} value={form.collection_mode}
                data-testid="invoice-collection-mode-select"
                onChange={(e) => setForm({ ...form, collection_mode: e.target.value })}>
                <option value="due">Leave due / collect later</option>
                <option value="full">Full payment now</option>
                <option value="split">Split payment now</option>
              </select>
            </Field>
            <Field label="Doctor (auto consultation fee)" className="sm:col-span-2">
              <select className={inputCls} value={form.doctor_id} data-testid="invoice-doctor-select"
                onChange={(e) => pickDoctor(e.target.value, form.fee_type)}>
                <option value="">No consultation charge</option>
                {doctors.map((d) => <option key={d.doctor_id} value={d.doctor_id}>Dr. {d.name}</option>)}
              </select>
            </Field>
            <Field label="Consultation type">
              <select className={inputCls} value={form.fee_type} data-testid="invoice-fee-type-select"
                disabled={!form.doctor_id}
                onChange={(e) => pickDoctor(form.doctor_id, e.target.value)}>
                <option value="new">First consultation</option>
                <option value="follow_up">Follow-up</option>
                <option value="emergency">Emergency</option>
              </select>
            </Field>
            <Field label="Reference (OPD / IPD ID)">
              <input className={inputCls} value={form.reference_id} data-testid="invoice-reference-input"
                onChange={(e) => setForm({ ...form, reference_id: e.target.value })} />
            </Field>
          </div>
          {feeNote && (
            <p className={`text-sm font-medium ${feeNote.includes("not configured") ? "text-red-600" : "text-emerald-700"}`}
              data-testid="invoice-fee-note">{feeNote}</p>
          )}

          <div>
            <div className="mb-3 flex items-center justify-between">
              <p className="overline text-[#EA580C]">Line items</p>
              <button type="button" onClick={() => setForm({ ...form, lines: [...form.lines, { ...EMPTY_LINE }] })}
                className={ghostBtnCls} data-testid="add-invoice-line-btn"><Plus className="h-3.5 w-3.5" /> Add item</button>
            </div>
            <div className="space-y-3">
              {form.lines.map((l, idx) => {
                const t = lineTotals(l);
                return (
                  <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4"
                    data-testid={`invoice-line-${idx}`}>
                    <div className="grid gap-3 sm:grid-cols-12">
                      <input placeholder="Item / service name *" className={`${inputCls} sm:col-span-4`}
                        data-testid={`invoice-line-desc-${idx}`} value={l.description}
                        onChange={(e) => setLine(idx, { description: e.target.value })} />
                      <input placeholder="Description" className={`${inputCls} sm:col-span-3`}
                        value={l.detail} onChange={(e) => setLine(idx, { detail: e.target.value })} />
                      <input placeholder="Qty" type="number" min="1" className={`${inputCls} sm:col-span-1`}
                        data-testid={`invoice-line-qty-${idx}`} value={l.quantity}
                        onChange={(e) => setLine(idx, { quantity: e.target.value })} />
                      <input placeholder="Rate" type="number" min="0" className={`${inputCls} sm:col-span-2`}
                        data-testid={`invoice-line-rate-${idx}`} value={l.rate}
                        onChange={(e) => setLine(idx, { rate: e.target.value })} />
                      <input placeholder="GST %" type="number" min="0" className={`${inputCls} sm:col-span-1`}
                        data-testid={`invoice-line-tax-${idx}`} value={l.tax_percent}
                        onChange={(e) => setLine(idx, { tax_percent: e.target.value })} />
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-sm text-slate-600">Line total
                        <span className="ml-2 font-semibold text-slate-900" data-testid={`invoice-line-total-${idx}`}>{inr(t.total)}</span>
                      </p>
                      {form.lines.length > 1 && (
                        <button type="button" onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-800"
                          data-testid={`remove-line-${idx}`}><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Bill tax % (on top of line tax)">
              <input type="number" min="0" className={inputCls} value={form.tax_percent}
                data-testid="invoice-tax-input" onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} />
            </Field>
            <Field label={`Bill discount (${form.discount_type === "percent" ? "%" : "₹"})`}>
              <div className="mb-2 flex gap-2" data-testid="invoice-discount-type-toggle">
                <button type="button" onClick={() => setForm({ ...form, discount_type: "amount" })}
                  data-testid="invoice-discount-amount-btn"
                  className={`${ghostBtnCls} ${form.discount_type === "amount"
                    ? "!border-[#EA580C] !text-[#C2410C]" : ""}`}>
                  <IndianRupee className="h-3.5 w-3.5" /> Amount
                </button>
                <button type="button" onClick={() => setForm({ ...form, discount_type: "percent" })}
                  data-testid="invoice-discount-percent-btn"
                  className={`${ghostBtnCls} ${form.discount_type === "percent"
                    ? "!border-[#EA580C] !text-[#C2410C]" : ""}`}>
                  <Percent className="h-3.5 w-3.5" /> Percentage
                </button>
              </div>
              <input type="number" min="0" step="0.01" className={inputCls} value={form.discount}
                max={form.discount_type === "percent" ? 100 : totals.invoiceValue}
                data-testid="invoice-discount-input" onChange={(e) => setForm({ ...form, discount: e.target.value })} />
              <p className="mt-1 text-xs text-slate-500" data-testid="invoice-discount-limit">
                Maximum: {form.discount_type === "percent" ? "100%" : inr(totals.invoiceValue)}
              </p>
            </Field>
            <Field label="Notes">
              <input className={inputCls} value={form.notes} data-testid="invoice-notes-input"
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>

          <div className="rounded-lg border border-slate-900 bg-[#0F172A] p-5 text-white">
            <div className="grid gap-3 sm:grid-cols-4 text-sm">
              {form.include_consultation && totals.consultation > 0 && (
                <div><p className="overline text-slate-400">Consultation</p><p className="mt-1 font-semibold">{inr(totals.consultation)}</p></div>
              )}
              <div><p className="overline text-slate-400">Taxable</p><p className="mt-1 font-semibold" data-testid="invoice-preview-subtotal">{inr(totals.subtotal)}</p></div>
              <div><p className="overline text-slate-400">Tax</p><p className="mt-1 font-semibold">{inr(totals.tax)}</p></div>
              <div><p className="overline text-slate-400">Discount</p><p className="mt-1 font-semibold">- {inr(totals.discount)}</p></div>
              <div><p className="overline text-[#EA580C]">Grand total</p>
                <p className="mt-1 font-ui text-xl font-bold" data-testid="invoice-preview-total">{inr(totals.total)}</p></div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">Indicative — the server recalculates and stores the authoritative total.</p>
          </div>

          {form.collection_mode === "full" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900" data-testid="full-payment-summary">
                Full payment: {inr(totals.total)} by {titleize(form.payment_method)}
              </p>
              <input className={`${inputCls} mt-3`} value={form.payment_reference}
                placeholder="Reference no. (optional)" data-testid="full-payment-reference-input"
                onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} />
            </div>
          )}
          {form.collection_mode === "split" && (
            <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-4"
              data-testid="invoice-split-payment-section">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">Split payment allocations</p>
                <button type="button" className={ghostBtnCls} data-testid="add-invoice-split-payment-btn"
                  onClick={() => setForm((current) => ({
                    ...current,
                    initial_payments: [...current.initial_payments,
                      { amount: "", mode: "upi", reference_no: "", note: "" }],
                  }))}>
                  <Plus className="h-3.5 w-3.5" /> Add payment mode
                </button>
              </div>
              {form.initial_payments.length === 0 && (
                <p className="mt-3 text-sm text-slate-500">Add Cash, UPI, Card or another payment mode.</p>
              )}
              <div className="mt-3 space-y-3">
                {form.initial_payments.map((payment, index) => (
                  <div className="grid gap-3 rounded-md border border-orange-100 bg-white p-3 sm:grid-cols-4"
                    key={index} data-testid={`invoice-split-payment-row-${index}`}>
                    <input required type="number" min="0.01" step="0.01" className={inputCls}
                      placeholder="Amount ₹" data-testid={`invoice-split-amount-${index}`}
                      value={payment.amount} onChange={(e) => updateInitialPayment(index, { amount: e.target.value })} />
                    <select className={inputCls} value={payment.mode}
                      data-testid={`invoice-split-mode-${index}`}
                      onChange={(e) => updateInitialPayment(index, { mode: e.target.value })}>
                      {PAYMENT_MODES.map((mode) => <option key={mode} value={mode}>{titleize(mode)}</option>)}
                    </select>
                    <input className={inputCls} placeholder="Reference no." value={payment.reference_no}
                      data-testid={`invoice-split-reference-${index}`}
                      onChange={(e) => updateInitialPayment(index, { reference_no: e.target.value })} />
                    <button type="button" className="text-left text-xs font-medium text-red-600"
                      data-testid={`remove-invoice-split-payment-${index}`}
                      onClick={() => setForm((current) => ({
                        ...current,
                        initial_payments: current.initial_payments.filter((_, itemIndex) => itemIndex !== index),
                      }))}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-sm text-slate-700" data-testid="invoice-split-total">
                Allocated: <b>{inr(initialPaymentTotal)}</b> / {inr(totals.total)}
              </p>
            </div>
          )}

          {formError && <p className="text-sm font-medium text-red-600" data-testid="invoice-form-error">{formError}</p>}
          <div className="flex flex-wrap gap-3">
            <button disabled={busy} className={primaryBtnCls} data-testid="invoice-submit-btn">Create invoice</button>
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(pay)} onClose={() => setPay(null)} title="Collect payment"
        subtitle={pay ? `${pay.invoice_no} · outstanding ${inr(pay.outstanding)}` : ""} testid="payment-modal">
        <form onSubmit={submitPayment} className="space-y-4">
          {payForm.payments.map((payment, index) => (
            <div className="grid gap-3 rounded-lg border border-slate-200 p-4 sm:grid-cols-3"
              key={index} data-testid={`split-payment-row-${index}`}>
              <Field label="Amount (₹) *">
                <input required type="number" min="0.01" step="0.01"
                  data-testid={`split-payment-amount-${index}`}
                  className={inputCls} value={payment.amount}
                  onChange={(e) => updatePayment(index, { amount: e.target.value })} />
              </Field>
              <Field label="Mode">
                <select className={inputCls} value={payment.mode}
                  data-testid={`split-payment-mode-${index}`}
                  onChange={(e) => updatePayment(index, { mode: e.target.value })}>
                  {PAYMENT_MODES.map((mode) => (
                    <option key={mode} value={mode}>{titleize(mode)}</option>
                  ))}
                </select>
              </Field>
              <Field label="Note">
                <input className={inputCls} value={payment.note}
                  data-testid={`split-payment-note-${index}`}
                  onChange={(e) => updatePayment(index, { note: e.target.value })} />
              </Field>
              <Field label="Reference no.">
                <input className={inputCls} value={payment.reference_no}
                  data-testid={`split-payment-reference-${index}`}
                  onChange={(e) => updatePayment(index, { reference_no: e.target.value })} />
              </Field>
              {payForm.payments.length > 1 && (
                <button type="button" className="text-left text-xs font-medium text-red-600 hover:text-red-800"
                  data-testid={`remove-split-payment-${index}`}
                  onClick={() => setPayForm((current) => ({
                    payments: current.payments.filter((_, paymentIndex) => paymentIndex !== index),
                  }))}>
                  Remove this split
                </button>
              )}
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button type="button" className={ghostBtnCls} data-testid="add-split-payment-btn"
              onClick={() => setPayForm((current) => ({
                payments: [...current.payments,
                  { amount: "", mode: "upi", reference_no: "", note: "" }],
              }))}>
              <Plus className="h-3.5 w-3.5" /> Add split
            </button>
            <p className="text-sm text-slate-600" data-testid="split-payment-total">
              Split total: <b>{inr(paymentTotal)}</b> / {inr(pay?.outstanding || 0)}
            </p>
          </div>
          <button className={primaryBtnCls} data-testid="payment-submit-btn">
            Record {payForm.payments.length > 1 ? "split payment" : "payment"}
          </button>
        </form>
      </Modal>

      <Modal open={Boolean(voidFor)} onClose={() => setVoidFor(null)} title="Void invoice"
        subtitle="The invoice is retained for audit with the reason and the reversed amount." testid="void-invoice-modal">
        <form onSubmit={submitVoid} className="space-y-5">
          <Field label="Reason (min 3 characters) *"><input required minLength={3} data-testid="void-reason-input" className={inputCls} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} /></Field>
          <button className={primaryBtnCls} data-testid="void-submit-btn">Void invoice</button>
        </form>
      </Modal>

      <PrintPreview open={Boolean(preview)} onClose={() => setPreview(null)}
        title={`Invoice ${preview?.invoice?.invoice_no || ""}`}
        subtitle={preview?.invoice?.patient_name} testid="invoice-print-preview">
        <HospitalInvoiceDoc invoice={preview?.invoice} patient={preview?.patient} business={preview?.business} />
      </PrintPreview>
    </div>
  );
}
